import { Bold, Code2, Heading1, Heading2, Italic, Link2, List as ListIcon, ListOrdered, Quote, Redo2, RemoveFormatting, Underline, Undo2, Unlink } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ClipboardEvent, MouseEvent } from "react";

import { sanitizeRichHtml } from "../../shared/safeHtml";
import { clearFormatting, editorRange, readFormatting, toggleBlock, unlinkSelection } from "./richTextCommands";

export function richTextToPlainText(html: string): string {
  const documentNode = new DOMParser().parseFromString(html, "text/html");
  function read(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
    if (!(node instanceof HTMLElement)) return "";
    const tag = node.tagName;
    if (tag === "BR") return "\n";
    const content = [...node.childNodes].map(read).join("");
    if (tag === "LI") {
      const parent = node.parentElement;
      const marker = parent?.tagName === "OL" ? `${[...parent.children].indexOf(node) + 1}. ` : "• ";
      return `${marker}${content.trim()}\n`;
    }
    if (["DIV", "P", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE", "TR"].includes(tag)) return `${content}\n`;
    return content;
  }
  return read(documentNode.body).replace(/\u00a0/g, " ").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function RichTextEditor({
  html,
  onChange,
  placeholder = "Write a reply…",
  ariaLabel = "Message",
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
  disabled = false,
}: {
  html: string;
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  ariaRequired?: boolean;
  disabled?: boolean;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<Range | null>(null);
  const [formatting, setFormatting] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && (html === "" || document.activeElement !== editor) && editor.innerHTML !== html) {
      editor.innerHTML = html;
      selectionRef.current = null;
      setFormatting({});
    }
  }, [html]);

  useEffect(() => {
    const update = () => {
      const editor = editorRef.current;
      if (!editor) return;
      const range = editorRange(editor);
      if (range) {
        selectionRef.current = range.cloneRange();
        setFormatting(readFormatting(editor));
      } else if (!editor.parentElement?.contains(document.activeElement)) {
        setFormatting({});
      }
    };
    document.addEventListener("selectionchange", update);
    return () => document.removeEventListener("selectionchange", update);
  }, []);

  function emitChange() {
    const nextHtml = editorRef.current?.innerHTML || "";
    onChange(nextHtml === "<br>" ? "" : nextHtml, richTextToPlainText(nextHtml));
    rememberSelection();
  }

  function rememberSelection() {
    const editor = editorRef.current;
    const range = editor && editorRange(editor);
    if (editor && range) {
      selectionRef.current = range.cloneRange();
      setFormatting(readFormatting(editor));
    }
  }

  function restoreSelection() {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    const saved = selectionRef.current;
    if (saved && selection && editor.contains(saved.startContainer) && editor.contains(saved.endContainer)) {
      selection.removeAllRanges();
      selection.addRange(saved);
    }
  }

  function run(command: string, value?: string) {
    if (disabled) return;
    restoreSelection();
    const editor = editorRef.current;
    if (!editor) return;
    if (command === "formatBlock" && value) toggleBlock(editor, value);
    else if (command === "removeFormat") clearFormatting(editor);
    else if (command === "unlink") unlinkSelection(editor);
    else document.execCommand(command, false, value);
    emitChange();
    rememberSelection();
  }

  function keepSelection(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    rememberSelection();
  }

  function addLink() {
    if (disabled) return;
    rememberSelection();
    const value = window.prompt("Enter an HTTPS URL or email address")?.trim();
    if (!value) return;
    const normalized = value.includes("@") && !/^\w+:/.test(value) ? `mailto:${value}` : value;
    if (!/^(https?:|mailto:)/i.test(normalized)) {
      window.alert("Use a complete http://, https://, or email address.");
      return;
    }
    restoreSelection();
    const editor = editorRef.current;
    const range = editor && editorRange(editor);
    if (!editor || !range) return;
    const parent = range.startContainer instanceof Element ? range.startContainer : range.startContainer.parentElement;
    const anchor = parent?.closest("a");
    if (range.collapsed && anchor && editor.contains(anchor)) {
      range.selectNodeContents(anchor);
      selectionRef.current = range.cloneRange();
    } else if (range.collapsed) {
      const escape = (text: string) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
      run("insertHTML", `<a href="${escape(normalized)}">${escape(value)}</a>`);
      return;
    }
    run("createLink", normalized);
  }

  function pasteContent(event: ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    if (disabled) return;
    const clipboardHtml = event.clipboardData.getData("text/html");
    const plain = event.clipboardData.getData("text/plain");
    const safe = clipboardHtml
      ? sanitizeRichHtml(clipboardHtml, 500_000)
      : plain.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\r?\n/g, "<br>");
    if (safe) document.execCommand("insertHTML", false, safe);
    emitChange();
    rememberSelection();
  }

  return (
    <div className={`rich-editor${disabled ? " is-disabled" : ""}`}>
      <div className="rich-editor__toolbar" role="toolbar" aria-label="Formatting tools">
        <button type="button" title="Bold" aria-label="Bold" aria-pressed={!!formatting.bold} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("bold")}><Bold aria-hidden="true" size={17} /></button>
        <button type="button" title="Italic" aria-label="Italic" aria-pressed={!!formatting.italic} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("italic")}><Italic aria-hidden="true" size={17} /></button>
        <button type="button" title="Underline" aria-label="Underline" aria-pressed={!!formatting.underline} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("underline")}><Underline aria-hidden="true" size={17} /></button>
        <button type="button" title="Heading 1" aria-label="Heading 1" aria-pressed={!!formatting.h1} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("formatBlock", "h1")}><Heading1 aria-hidden="true" size={17} /></button>
        <button type="button" title="Heading 2" aria-label="Heading 2" aria-pressed={!!formatting.h2} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("formatBlock", "h2")}><Heading2 aria-hidden="true" size={17} /></button>
        <button type="button" title="Quote" aria-label="Quote" aria-pressed={!!formatting.blockquote} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("formatBlock", "blockquote")}><Quote aria-hidden="true" size={17} /></button>
        <button type="button" title="Code block" aria-label="Code block" aria-pressed={!!formatting.pre} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("formatBlock", "pre")}><Code2 aria-hidden="true" size={17} /></button>
        <span className="rich-editor__separator" aria-hidden="true" />
        <button type="button" title="Undo" aria-label="Undo" disabled={disabled} onMouseDown={keepSelection} onClick={() => run("undo")}><Undo2 aria-hidden="true" size={17} /></button>
        <button type="button" title="Redo" aria-label="Redo" disabled={disabled} onMouseDown={keepSelection} onClick={() => run("redo")}><Redo2 aria-hidden="true" size={17} /></button>
        <span className="rich-editor__separator" aria-hidden="true" />
        <button type="button" title="Bulleted list" aria-label="Bulleted list" aria-pressed={!!formatting.insertUnorderedList} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("insertUnorderedList")}><ListIcon aria-hidden="true" size={17} /></button>
        <button type="button" title="Numbered list" aria-label="Numbered list" aria-pressed={!!formatting.insertOrderedList} disabled={disabled} onMouseDown={keepSelection} onClick={() => run("insertOrderedList")}><ListOrdered aria-hidden="true" size={17} /></button>
        <span className="rich-editor__separator" aria-hidden="true" />
        <button type="button" title="Insert link" aria-label="Insert link" aria-pressed={!!formatting.link} disabled={disabled} onMouseDown={keepSelection} onClick={addLink}><Link2 aria-hidden="true" size={17} /></button>
        <button type="button" title="Remove link" aria-label="Remove link" disabled={disabled} onMouseDown={keepSelection} onClick={() => run("unlink")}><Unlink aria-hidden="true" size={17} /></button>
        <button type="button" title="Clear formatting" aria-label="Clear formatting" disabled={disabled} onMouseDown={keepSelection} onClick={() => run("removeFormat")}><RemoveFormatting aria-hidden="true" size={17} /></button>
      </div>
      <div
        ref={editorRef}
        className="rich-editor__surface rich-text-content"
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        data-placeholder={placeholder}
        onInput={emitChange}
        onKeyUp={rememberSelection}
        onMouseUp={rememberSelection}
        onBlur={rememberSelection}
        onPaste={pasteContent}
        suppressContentEditableWarning
      />
    </div>
  );
}
